import PropTypes from 'prop-types'
import { gql } from '@apollo/client'
import { artistFragment, artistFragmentPropTypes } from '~/graphql/fragments/artist'
import organizerBrandFragment, {
  organizerBrandFragmentPropTypes,
} from '~/graphql/fragments/organizerBrand'
import eventVideoFragment, { eventVideoPropTypes } from '~/graphql/fragments/eventVideo'

// Event-data shared between event and event-type pages.
export const sharedEventDataFragment = gql`
  fragment sharedEventData on Event {
    id
    slug
    name
    status
    isSellingBlocked
    isBuyingBlocked
    isPopular
    category
    instagramUsername
    startDate
    endDate
    facebookEventWalls {
      facebookUrl
      isMainEventWall
    }
    isVerified
    seoMetadata {
      title
      description
    }
    description
    eventVideo {
      ...eventVideo
    }
    alias {
      uri {
        url
        path
      }
    }
    headerImageUrl
    imageUrl
    imageSmallUrl
    organizerShop {
      id
      organizerBranding {
        name
        image
      }
      hasDynamicProducts
    }
    location {
      id
      slug
      name
      geoInfo {
        latitude
        longitude
      }
      background
      amountOfActiveUpcomingEvents
      image
      website
      averageFanExperienceRating
      totalAmountOfFanExperiences
      city {
        id
        slug
        name
      }
      country {
        name
      }
    }
    organizerBrands {
      ...organizerBrand
    }
    artists {
      ...artist
    }
    types(first: 99) {
      edges {
        node {
          id
          slug
        }
      }
    }
    warning {
      title
      message
      url {
        text
        url
      }
    }
  }

  ${artistFragment}
  ${organizerBrandFragment}
  ${eventVideoFragment}
`

export const sharedEventDataFragmentPropTypes = {
  id: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['ACTIVE', 'ON_HOLD', 'EXPIRED', 'CANCELLED']).isRequired,
  isSellingBlocked: PropTypes.bool.isRequired,
  isBuyingBlocked: PropTypes.bool,
  isPopular: PropTypes.bool,
  category: PropTypes.oneOf([
    'CONCERTS',
    'FESTIVALS',
    'CLUBS',
    'SPORTS',
    'THEATRE_AND_COMEDY',
    'TRANSPORTATION',
    'VOUCHERS_AND_DAY_OUT',
  ]),
  instagramUsername: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string,
  facebookEventWalls: PropTypes.arrayOf(
    PropTypes.shape({
      facebookUrl: PropTypes.string.isRequired,
      isMainEventWall: PropTypes.bool.isRequired,
    })
  ),
  isVerified: PropTypes.bool.isRequired,
  seoMetadata: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  description: PropTypes.string,
  eventVideo: eventVideoPropTypes,
  alias: PropTypes.shape({
    uri: PropTypes.shape({
      url: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
    }).isRequired,
  }),
  headerImageUrl: PropTypes.string,
  imageUrl: PropTypes.string,
  imageSmallUrl: PropTypes.string,
  organizerShop: PropTypes.shape({
    id: PropTypes.string.isRequired,
    organizerBranding: PropTypes.shape({
      name: PropTypes.string.isRequired,
      image: PropTypes.string,
    }).isRequired,
    hasDynamicProducts: PropTypes.bool.isRequired,
  }),
  location: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    background: PropTypes.string,
    amountOfActiveUpcomingEvents: PropTypes.number.isRequired,
    image: PropTypes.string,
    website: PropTypes.string,
    address: PropTypes.string,
    zipcode: PropTypes.string,
    city: PropTypes.shape({
      id: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    country: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  }),
  organizerBrands: PropTypes.arrayOf(organizerBrandFragmentPropTypes).isRequired,
  artists: PropTypes.arrayOf(artistFragmentPropTypes).isRequired,
  types: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired,
          slug: PropTypes.string.isRequired,
        }),
      })
    ),
  }),
  warning: PropTypes.shape({
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    url: PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  }),
}
